import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import ShieldIcon from '@mui/icons-material/Shield';
import SyncIcon from '@mui/icons-material/Sync';
import { useTranslation } from 'react-i18next';
import IconPicker from '../../components/IconPicker';
import { getSidebarIcon } from '../../icons/sidebarIcons';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

const COPY = {
  ar: {
    title: 'مركز الصلاحيات والقائمة',
    subtitle: 'صفحة موحدة لإدارة الأدوار، روابط القائمة، وصلاحيات الوصول بدون الرجوع للصفحات القديمة.',
    roles: 'الأدوار',
    menu: 'القائمة والصفحات',
    permissions: 'صلاحيات الدور',
    addRole: 'دور جديد',
    editRole: 'تعديل الدور',
    roleName: 'اسم الدور بالإنجليزية',
    roleNameAr: 'اسم الدور بالعربية',
    addPage: 'صفحة أو رابط جديد',
    editPage: 'تعديل رابط القائمة',
    textKey: 'مفتاح النص',
    path: 'المسار',
    pageName: 'مفتاح الصفحة',
    permissionKey: 'مفتاح الصلاحية',
    icon: 'الأيقونة',
    sort: 'الترتيب',
    active: 'نشط',
    shownInMenu: 'يظهر في القائمة',
    save: 'حفظ',
    cancel: 'إلغاء',
    search: 'بحث',
    chooseRole: 'اختر الدور',
    enabled: 'مفعلة',
    disabled: 'غير مفعلة',
    sync: 'مزامنة الصفحات',
    legacyHidden: 'الصفحات القديمة مخفية من الواجهة فقط، ولا تزال متاحة للرجوع لها يدويا.',
    noRole: 'اختر دورا لعرض صلاحياته.',
    saved: 'تم الحفظ بنجاح.',
    simpleMode: 'وضع الخدمات',
    advancedMode: 'تخصيص الفرع',
    simpleHint: 'فعّل الفرع الرئيسي لتطبيق الصلاحية على صفحاته التابعة، أو افتح تخصيص الفرع لاختيار صفحات محددة منه فقط.',
    mainPage: 'الصفحة الرئيسية',
    secondaryPages: 'الصفحات التابعة',
    enabledPages: 'صفحات مفعلة',
    noSecondaryPages: 'لا توجد صفحات تابعة',
    showUnusedPages: 'إظهار الصفحات غير المرتبطة',
    hideUnusedPages: 'إخفاء الصفحات غير المرتبطة',
    unusedPages: 'صفحات غير مرتبطة بالقائمة',
    visiblePages: 'صفحات ظاهرة',
    linkedPage: 'مرتبطة بالقائمة',
    unusedPage: 'غير مرتبطة',
    restoreAsMenuLink: 'إرجاع كرابط قائمة',
    displayName: 'الاسم الواضح',
    description: 'الوصف',
    technicalName: 'اسم الصفحة',
    status: 'الحالة',
    customizeBranch: 'تخصيص هذا الفرع',
    closeBranch: 'إغلاق التخصيص',
    branchPermissions: 'صلاحيات الفرع',
    roleScope: 'اختر الدور أولا',
    roleScopeHint: 'الصلاحيات تدار حسب الدور المعروف في النظام، مثل المدير أو مالك الخيل أو الطبيب أو المحاسب.',
    sidebarMenus: 'القائمة الجانبية المتاحة',
    sidebarMenusHint: 'اختر الروابط التي تظهر لهذا الدور في القائمة الجانبية. تفعيل الرابط يفعّل الصفحة المرتبطة به أيضا.',
    pagesForRole: 'صفحات الدور',
    menuWithoutPage: 'رابط بدون صفحة مرتبطة',
    linkedPages: 'صفحات مرتبطة',
    enabledMenus: 'روابط مفعلة',
    technicalPage: 'الصفحة الفنية',
    adminRequired: 'هذه العملية تتطلب تسجيل الدخول بحساب مدير النظام. إذا كنت تستخدم حساب المدير، سجل خروج ثم ادخل مرة أخرى لتحديث الجلسة.',
  },
  en: {
    title: 'Access Control Center',
    subtitle: 'Unified role, menu, and page permission management without removing the legacy pages.',
    roles: 'Roles',
    menu: 'Menu & Pages',
    permissions: 'Role permissions',
    addRole: 'New role',
    editRole: 'Edit role',
    roleName: 'English role name',
    roleNameAr: 'Arabic role name',
    addPage: 'New page or menu link',
    editPage: 'Edit menu link',
    textKey: 'Text key',
    path: 'Path',
    pageName: 'Page key',
    permissionKey: 'Permission key',
    icon: 'Icon',
    sort: 'Sort',
    active: 'Active',
    shownInMenu: 'Shown in menu',
    save: 'Save',
    cancel: 'Cancel',
    search: 'Search',
    chooseRole: 'Choose role',
    enabled: 'Enabled',
    disabled: 'Disabled',
    sync: 'Sync pages',
    legacyHidden: 'Legacy pages are hidden from the UI only, and remain available for manual fallback.',
    noRole: 'Choose a role to view its permissions.',
    saved: 'Saved successfully.',
    simpleMode: 'Service mode',
    advancedMode: 'Branch customization',
    simpleHint: 'Enable a main branch to apply access to its secondary pages, or open that branch to choose specific pages only.',
    mainPage: 'Main page',
    secondaryPages: 'Secondary pages',
    enabledPages: 'Enabled pages',
    noSecondaryPages: 'No secondary pages',
    showUnusedPages: 'Show unlinked pages',
    hideUnusedPages: 'Hide unlinked pages',
    unusedPages: 'Unlinked pages',
    visiblePages: 'Visible pages',
    linkedPage: 'Linked in menu',
    unusedPage: 'Unlinked',
    restoreAsMenuLink: 'Restore as menu link',
    displayName: 'Display name',
    description: 'Description',
    technicalName: 'Page name',
    status: 'Status',
    customizeBranch: 'Customize this branch',
    closeBranch: 'Close customization',
    branchPermissions: 'Branch permissions',
    roleScope: 'Choose role first',
    roleScopeHint: 'Permissions are managed by the known system role, such as manager, owner, veterinarian, or accountant.',
    sidebarMenus: 'Available sidebar menus',
    sidebarMenusHint: 'Choose the links shown for this role in the sidebar. Enabling a link also enables its mapped page.',
    pagesForRole: 'Role pages',
    menuWithoutPage: 'Menu link without mapped page',
    linkedPages: 'Mapped pages',
    enabledMenus: 'Enabled menus',
    technicalPage: 'Technical page',
    adminRequired: 'This action requires a system administrator session. If you are using an admin account, sign out and sign back in to refresh the session.',
  },
};

const GROUPS = [
  {
    key: 'veterinary',
    ar: 'الطب البيطري',
    en: 'Veterinary medicine',
    tokens: ['vet', 'clinic', 'checkup', 'medical', 'medication', 'procedure', 'record'],
    mainCandidates: ['recordlist', 'vetcheckupview', 'vetclinicform', 'procedures', 'medications'],
  },
  {
    key: 'reproduction',
    ar: 'طب الإنتاج',
    en: 'Reproduction medicine',
    tokens: ['reproduction', 'insemination', 'stallion', 'breeding'],
    mainCandidates: ['reproductionpage', 'reproductionprocedureslist', 'directinseminationform', 'stallionform'],
  },
  {
    key: 'owners',
    ar: 'الملاك والخيل',
    en: 'Owners & horses',
    tokens: ['owner', 'client', 'horse', 'pedigree'],
    mainCandidates: ['horse-list', 'horsesclientslist', 'owner-list', 'clientslist'],
  },
  {
    key: 'training',
    ar: 'تدريب الخيل والبطولات',
    en: 'Horse training & shows',
    tokens: ['training', 'schedule', 'competition', 'championship', 'show', 'award', 'medal'],
    mainCandidates: ['horsecompetitionspage', 'training-management', 'training-record', 'training_schedule'],
  },
  {
    key: 'farrier',
    ar: 'البيطار',
    en: 'Farrier',
    tokens: ['farrier'],
    mainCandidates: ['farrierlist', 'addfarrierrecord', 'farrier-procedures', 'farrier-medications'],
  },
  {
    key: 'accounting',
    ar: 'المحاسب والمالية',
    en: 'Accounting & finance',
    tokens: ['account', 'finance', 'ledger', 'invoice', 'expense'],
    mainCandidates: ['accountantreview', 'financesummary', 'horsefinancecenter', 'accounting/finance/summary'],
  },
  {
    key: 'site_admin',
    ar: 'إدارة الموقع',
    en: 'Site administration',
    tokens: ['user', 'role', 'permission', 'menu', 'access', 'admin'],
    mainCandidates: ['access-control', 'rolelist', 'user-list', 'userregistration'],
  },
  {
    key: 'preferences',
    ar: 'تفضيلات عامة',
    en: 'General preferences',
    tokens: ['setting', 'system', 'pricing', 'price', 'alert', 'restore', 'template', 'profile', 'password', 'color', 'breed', 'country', 'stable', 'room', 'accommodation', 'transport'],
    mainCandidates: ['systemsettings', 'settings', 'available-rooms', 'stable-list'],
  },
  { key: 'other', ar: 'أخرى', en: 'Other', tokens: [], mainCandidates: [] },
];

const legacyPaths = new Set(['/roles-permissions', '/role-permissions', '/admin/menu-designer']);

const PAGE_LABELS = {
  home: { ar: 'الرئيسية', en: 'Home' },
  dashboard: { ar: 'لوحة التحكم', en: 'Dashboard' },
  statistics: { ar: 'الإحصائيات', en: 'Statistics' },
  'horse-list': { ar: 'قائمة الخيل', en: 'Horse list' },
  'horse-add': { ar: 'إضافة خيل', en: 'Add horse' },
  'register-horse': { ar: 'تسجيل خيل', en: 'Register horse' },
  'horse-edit': { ar: 'تعديل بيانات خيل', en: 'Edit horse' },
  'horse-details': { ar: 'تفاصيل الخيل', en: 'Horse details' },
  horsehistory: { ar: 'تاريخ الخيل', en: 'Horse history' },
  'my-horses': { ar: 'خيولي', en: 'My horses' },
  myhorses: { ar: 'خيولي', en: 'My horses' },
  'my-horse-stats': { ar: 'إحصائياتي', en: 'My statistics' },
  myhorsestats: { ar: 'إحصائياتي', en: 'My statistics' },
  horsecompetitionspage: { ar: 'بطولات الخيل', en: 'Horse competitions' },
  pedigreetree: { ar: 'شجرة النسب', en: 'Pedigree tree' },
  pedigree_tree: { ar: 'شجرة النسب', en: 'Pedigree tree' },
  horsesclientslist: { ar: 'خيل العملاء', en: 'Client horses' },
  clientslist: { ar: 'قائمة العملاء', en: 'Clients list' },
  'owner-list': { ar: 'قائمة الملاك', en: 'Owners list' },
  'owner-details': { ar: 'تفاصيل المالك', en: 'Owner details' },
  'owner-edit': { ar: 'تعديل المالك', en: 'Edit owner' },
  'owner-horses': { ar: 'خيل المالك', en: 'Owner horses' },
  ownerhorseslist: { ar: 'خيل المالك', en: 'Owner horses' },
  'register-owner': { ar: 'تسجيل مالك', en: 'Register owner' },
  'available-rooms': { ar: 'الغرف المتاحة', en: 'Available rooms' },
  accommodations: { ar: 'السكن', en: 'Accommodation' },
  'stable-list': { ar: 'قائمة الإسطبلات', en: 'Stable list' },
  'stable-details': { ar: 'تفاصيل الإسطبل', en: 'Stable details' },
  'stable-room-list': { ar: 'غرف الإسطبل', en: 'Stable rooms' },
  'stable-rooms': { ar: 'غرف الإسطبل', en: 'Stable rooms' },
  'room-details': { ar: 'تفاصيل الغرفة', en: 'Room details' },
  'stables/add': { ar: 'إضافة إسطبل', en: 'Add stable' },
  horsetransportreportpage: { ar: 'تقرير نقل الخيل', en: 'Horse transport report' },
  horsetransportform: { ar: 'طلب نقل خيل', en: 'Horse transport form' },
  recordlist: { ar: 'سجلات العيادة', en: 'Clinic records' },
  'record-details': { ar: 'تفاصيل سجل العيادة', en: 'Clinic record details' },
  procedures: { ar: 'إجراءات العيادة', en: 'Clinic procedures' },
  'procedure-add': { ar: 'إضافة إجراء عيادة', en: 'Add clinic procedure' },
  procedureedit: { ar: 'تعديل إجراء عيادة', en: 'Edit clinic procedure' },
  medications: { ar: 'أدوية العيادة', en: 'Clinic medications' },
  'medication-add': { ar: 'إضافة دواء عيادة', en: 'Add clinic medication' },
  'medication-edit': { ar: 'تعديل دواء عيادة', en: 'Edit clinic medication' },
  vetcheckupview: { ar: 'الفحوصات البيطرية', en: 'Vet checkups' },
  vetclinicform: { ar: 'إضافة سجل بيطري', en: 'Add vet record' },
  vetclinicrecordinside: { ar: 'سجل بيطري داخلي', en: 'Inside vet record' },
  vetclinicrecordoutside: { ar: 'سجل بيطري خارجي', en: 'Outside vet record' },
  vetclinicrecordedit: { ar: 'تعديل سجل بيطري', en: 'Edit vet record' },
  clinicapprovals: { ar: 'اعتمادات العيادة', en: 'Clinic approvals' },
  checkuptypespage: { ar: 'أنواع الفحوصات', en: 'Checkup types' },
  farrierlist: { ar: 'سجلات البيطار', en: 'Farrier records' },
  farrierdetails: { ar: 'تفاصيل سجل البيطار', en: 'Farrier details' },
  addfarrierrecord: { ar: 'إضافة سجل بيطار', en: 'Add farrier record' },
  addfarrierrecordinside: { ar: 'إضافة سجل بيطار داخلي', en: 'Add inside farrier record' },
  addfarrierrecordoutside: { ar: 'إضافة سجل بيطار خارجي', en: 'Add outside farrier record' },
  editfarrierrecord: { ar: 'تعديل سجل البيطار', en: 'Edit farrier record' },
  'farrier-medications': { ar: 'أدوية البيطار', en: 'Farrier medications' },
  'farrier-medication-add': { ar: 'إضافة دواء بيطار', en: 'Add farrier medication' },
  'farrier-medication-edit': { ar: 'تعديل دواء بيطار', en: 'Edit farrier medication' },
  'farrier-procedures': { ar: 'إجراءات البيطار', en: 'Farrier procedures' },
  'farrier-procedure-add': { ar: 'إضافة إجراء بيطار', en: 'Add farrier procedure' },
  'farrier-procedure-edit': { ar: 'تعديل إجراء بيطار', en: 'Edit farrier procedure' },
  reproductionpage: { ar: 'سجلات الإنتاج', en: 'Breeding records' },
  reproductionprocedureslist: { ar: 'إجراءات الإنتاج', en: 'Breeding procedures' },
  addreproductionprocedure: { ar: 'إضافة إجراء إنتاج', en: 'Add breeding procedure' },
  editreproductionprocedure: { ar: 'تعديل إجراء إنتاج', en: 'Edit breeding procedure' },
  'reproduction-details': { ar: 'تفاصيل الإنتاج', en: 'Breeding details' },
  reproductionrecorddetails: { ar: 'تفاصيل سجل الإنتاج', en: 'Breeding record details' },
  directinseminationform: { ar: 'التلقيح المباشر', en: 'Direct insemination' },
  stallionform: { ar: 'بيانات الفحل', en: 'Stallion form' },
  accountantreview: { ar: 'مراجعة المحاسب', en: 'Accountant review' },
  financesummary: { ar: 'الملخص المالي', en: 'Finance summary' },
  'accounting/finance/summary': { ar: 'ملخص المالية', en: 'Finance summary' },
  'accounting/horse-finance': { ar: 'مالية الخيل', en: 'Horse finance' },
  horsefinancecenter: { ar: 'مركز مالية الخيل', en: 'Horse finance center' },
  horseledgerinsidepage: { ar: 'دفتر خدمات الخيل', en: 'Horse service ledger' },
  clientaccountantreport: { ar: 'تقرير محاسبة العميل', en: 'Client accounting report' },
  expensespage: { ar: 'المصاريف', en: 'Expenses' },
  'access-control': { ar: 'مركز الصلاحيات والقائمة', en: 'Access control center' },
  'settings/access-control': { ar: 'مركز الصلاحيات والقائمة', en: 'Access control center' },
  rolelist: { ar: 'قائمة الأدوار', en: 'Role list' },
  'role-permissions': { ar: 'صلاحيات الدور القديمة', en: 'Legacy role permissions' },
  'roles-permissions': { ar: 'إدارة الصلاحيات القديمة', en: 'Legacy permissions manager' },
  userlist: { ar: 'قائمة المستخدمين', en: 'User list' },
  'user-list': { ar: 'قائمة المستخدمين', en: 'User list' },
  userregistration: { ar: 'تسجيل مستخدم', en: 'User registration' },
  useredit: { ar: 'تعديل مستخدم', en: 'Edit user' },
  'user-details': { ar: 'تفاصيل المستخدم', en: 'User details' },
  userpermissions: { ar: 'صلاحيات المستخدم', en: 'User permissions' },
  userprofile: { ar: 'ملف المستخدم', en: 'User profile' },
  profile: { ar: 'الملف الشخصي', en: 'Profile' },
  settings: { ar: 'الإعدادات', en: 'Settings' },
  systemsettings: { ar: 'الإعدادات العامة', en: 'General settings' },
  'settings/backups': { ar: 'النسخ الاحتياطي', en: 'Database backup' },
  settingsbackups: { ar: 'النسخ الاحتياطي', en: 'Database backup' },
  'settings/pricing': { ar: 'إدارة الأسعار', en: 'Pricing control' },
  settingspricing: { ar: 'إدارة الأسعار', en: 'Pricing control' },
  'alerts/templates': { ar: 'قوالب التنبيهات', en: 'Alert templates' },
  alertstemplates: { ar: 'قوالب التنبيهات', en: 'Alert templates' },
  owneralerttemplates: { ar: 'قوالب تنبيهات الملاك', en: 'Owner alert templates' },
  restoredeletedhorses: { ar: 'تفعيل الخيل المعطلة', en: 'Reactivate hidden horses' },
  changepassword: { ar: 'تغيير كلمة المرور', en: 'Change password' },
  'admin/menu-designer': { ar: 'مصمم القائمة القديم', en: 'Legacy menu designer' },
};

const WORD_LABELS = {
  ar: {
    add: 'إضافة',
    edit: 'تعديل',
    list: 'قائمة',
    details: 'تفاصيل',
    report: 'تقرير',
    horse: 'خيل',
    horses: 'الخيل',
    owner: 'مالك',
    owners: 'الملاك',
    client: 'عميل',
    clients: 'العملاء',
    user: 'مستخدم',
    users: 'المستخدمين',
    role: 'دور',
    roles: 'الأدوار',
    permission: 'صلاحية',
    permissions: 'الصلاحيات',
    record: 'سجل',
    records: 'السجلات',
    clinic: 'العيادة',
    vet: 'بيطري',
    farrier: 'البيطار',
    reproduction: 'الإنتاج',
    procedure: 'إجراء',
    procedures: 'الإجراءات',
    medication: 'دواء',
    medications: 'الأدوية',
    stable: 'إسطبل',
    stables: 'الإسطبلات',
    room: 'غرفة',
    rooms: 'الغرف',
    accommodation: 'السكن',
    transport: 'النقل',
    finance: 'المالية',
    accounting: 'المحاسبة',
    summary: 'ملخص',
    settings: 'الإعدادات',
    system: 'النظام',
    alert: 'تنبيه',
    alerts: 'التنبيهات',
    template: 'قالب',
    templates: 'القوالب',
    competition: 'بطولة',
    competitions: 'البطولات',
    profile: 'الملف',
    history: 'التاريخ',
  },
  en: {},
};

function compact(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function normalizePageName(value) {
  return String(value || '').trim().replace(/^\/+/, '').toLowerCase();
}

function wordsFromPageName(value) {
  return normalizePageName(value)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[/_-]+/g, ' ')
    .replace(/([a-z]+)(list|page|form|details|record|records|edit|add|summary|report)$/i, '$1 $2')
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean);
}

function titleCase(value) {
  return String(value || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function fallbackPageLabel(pageName, lang) {
  const words = wordsFromPageName(pageName);
  if (lang === 'ar') {
    return words.map((word) => WORD_LABELS.ar[word.toLowerCase()] || word).join(' ');
  }
  return titleCase(words.join(' '));
}

function getPageMeta(page, isArabic) {
  const lang = isArabic ? 'ar' : 'en';
  const key = normalizePageName(page?.page_name);
  const label = PAGE_LABELS[key]?.[lang] || PAGE_LABELS[compact(key)]?.[lang] || fallbackPageLabel(key, lang);
  const group = groupFor(key);
  const groupName = isArabic ? group.ar : group.en;
  return {
    label,
    description: isArabic
      ? `صفحة ${label} ضمن خدمة ${groupName}.`
      : `${label} page under ${groupName}.`,
  };
}

function groupFor(pageName) {
  const normalized = compact(pageName);
  const matches = GROUPS
    .filter((group) => group.key !== 'other')
    .map((group) => ({
      group,
      score: Math.max(0, ...group.tokens.map((token) => (normalized.includes(compact(token)) ? compact(token).length : 0))),
    }))
    .filter((match) => match.score > 0)
    .sort((a, b) => b.score - a.score);
  return matches[0]?.group || GROUPS[GROUPS.length - 1];
}

function pickMainPage(group, pages) {
  const candidates = (group.mainCandidates || []).map(compact);
  const match = candidates
    .map((candidate) =>
      pages.find((page) =>
        [page.page_name, page.path, page.text_key]
          .map(compact)
          .some((value) => value === candidate || value.endsWith(candidate))
      )
    )
    .find(Boolean);
  return match || pages[0];
}

function normalizePath(value) {
  const path = String(value || '').trim();
  if (!path) return '';
  return ('/' + path).replace(/\/+/g, '/').toLowerCase();
}

async function fetchJSON(path, options = {}) {
  const token = localStorage.getItem('token') || '';
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (_) {
    data = text ? { message: text } : null;
  }
  if (!res.ok) {
    throw new Error(data?.details || data?.message || data?.error || `HTTP ${res.status}`);
  }
  return data;
}

function Metric({ label, value, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 94 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: soft,
            color,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <ShieldIcon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color, fontWeight: 900 }}>{label}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{value}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function AccessControlCenter() {
  const { i18n } = useTranslation();
  const text = COPY[i18n.language === 'ar' ? 'ar' : 'en'];
  const isArabic = i18n.language === 'ar';
  const [tab, setTab] = useState(0);
  const [overview, setOverview] = useState({ roles: [], menu: [], pages: [], rolePermissions: [] });
  const [selectedRole, setSelectedRole] = useState('');
  const [permissionDraft, setPermissionDraft] = useState({});
  const [expandedServices, setExpandedServices] = useState({});
  const [showUnusedPages, setShowUnusedPages] = useState(false);
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [roleDialog, setRoleDialog] = useState({ open: false, role: null });
  const [menuDialog, setMenuDialog] = useState({ open: false, item: null });
  const [iconOpen, setIconOpen] = useState(false);
  const isSystemAdminSession = Number(localStorage.getItem('role_id') || 0) === 1;

  const actionErrorMessage = (e) => (
    e?.message === 'Access denied' || e?.message === 'HTTP 403'
      ? text.adminRequired
      : (e?.message || text.adminRequired)
  );

  const loadOverview = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchJSON('/access-control/overview');
      setOverview(data || { roles: [], menu: [], pages: [], rolePermissions: [] });
      if (!selectedRole && data?.roles?.[0]?.role_id) setSelectedRole(String(data.roles[0].role_id));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPermissionDraft({});
  }, [selectedRole]);

  const pageCatalog = useMemo(() => {
    const byName = new Map();
    (overview.pages || []).forEach((page) => {
      if (!page.page_name) return;
      const current = byName.get(page.page_name) || {};
      byName.set(page.page_name, { ...current, ...page });
    });
    return [...byName.values()].sort((a, b) => String(a.page_name).localeCompare(String(b.page_name), 'en', { numeric: true }));
  }, [overview.pages]);

  const activeMenuPageNames = useMemo(() => {
    return new Set(
      (overview.menu || [])
        .filter((item) => Number(item.is_active ?? 1) === 1 && item.page_name)
        .map((item) => normalizePageName(item.page_name))
        .filter(Boolean)
    );
  }, [overview.menu]);

  const visiblePageCatalog = useMemo(() => {
    return showUnusedPages
      ? pageCatalog
      : pageCatalog.filter((page) => activeMenuPageNames.has(normalizePageName(page.page_name)));
  }, [activeMenuPageNames, pageCatalog, showUnusedPages]);

  const pageByName = useMemo(() => {
    const map = new Map();
    pageCatalog.forEach((page) => map.set(normalizePageName(page.page_name), page));
    return map;
  }, [pageCatalog]);

  const unusedPageCount = useMemo(() => {
    return pageCatalog.filter((page) => !activeMenuPageNames.has(normalizePageName(page.page_name))).length;
  }, [activeMenuPageNames, pageCatalog]);

  const permissionMap = useMemo(() => {
    const map = new Map();
    (overview.rolePermissions || [])
      .filter((row) => String(row.role_id) === String(selectedRole))
      .forEach((row) => map.set(normalizePageName(row.page_name), Number(row.can_access) === 1));
    return map;
  }, [overview.rolePermissions, selectedRole]);

  const menuCatalog = useMemo(() => {
    const byId = new Map();
    (overview.menu || []).forEach((item) => {
      const id = item.menu_id;
      if (!id) return;
      const current = byId.get(id) || { ...item, page_names: [] };
      if (item.page_name) {
        const page = normalizePageName(item.page_name);
        if (page && !current.page_names.includes(page)) current.page_names.push(page);
      }
      byId.set(id, { ...current, ...item, page_names: current.page_names });
    });

    return [...byId.values()]
      .filter((item) => Number(item.is_active ?? 1) === 1)
      .sort((a, b) => (Number(a.sort_order || 0) - Number(b.sort_order || 0)) || Number(a.menu_id) - Number(b.menu_id));
  }, [overview.menu]);

  const serviceGroups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return GROUPS.map((group) => {
      const pages = visiblePageCatalog
        .filter((page) => groupFor(page.page_name).key === group.key)
        .sort((a, b) => getPageMeta(a, isArabic).label.localeCompare(getPageMeta(b, isArabic).label, isArabic ? 'ar' : 'en', { numeric: true }));
      if (!pages.length) return null;
      const hay = [
        group.ar,
        group.en,
        ...pages.flatMap((page) => {
          const meta = getPageMeta(page, isArabic);
          return [page.page_name, page.path, page.text_key, meta.label, meta.description];
        }),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (needle && !hay.includes(needle)) return null;
      const mainPage = pickMainPage(group, pages);
      const secondaryPages = pages.filter((page) => page.page_name !== mainPage?.page_name);
      const enabled = pages.filter((page) => permissionDraft[normalizePageName(page.page_name)] ?? permissionMap.get(normalizePageName(page.page_name)) ?? false).length;
      return {
        ...group,
        pages,
        mainPage,
        secondaryPages,
        enabled,
        total: pages.length,
      };
    }).filter(Boolean);
  }, [isArabic, permissionDraft, permissionMap, query, visiblePageCatalog]);

  const enabledCount = useMemo(() => {
    if (!selectedRole) return 0;
    return visiblePageCatalog.filter((page) => permissionDraft[normalizePageName(page.page_name)] ?? permissionMap.get(normalizePageName(page.page_name))).length;
  }, [permissionDraft, permissionMap, selectedRole, visiblePageCatalog]);

  const enabledMenuCount = useMemo(() => {
    if (!selectedRole) return 0;
    return menuCatalog.filter((item) => {
      const pages = item.page_names || [];
      if (!pages.length) return false;
      return pages.some((pageName) => permissionDraft[pageName] ?? permissionMap.get(pageName) ?? false);
    }).length;
  }, [menuCatalog, permissionDraft, permissionMap, selectedRole]);

  const menuRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (overview.menu || []).filter((item) => {
      const hay = [item.text_key, item.path, item.permission_key, item.page_name].filter(Boolean).join(' ').toLowerCase();
      return !needle || hay.includes(needle);
    });
  }, [overview.menu, query]);

  const saveRole = async () => {
    setError('');
    setMessage('');
    try {
      const role = roleDialog.role || {};
      const body = {
        role_name: role.role_name,
        role_name_ar: role.role_name_ar,
      };
      await fetchJSON(role.role_id ? `/access-control/roles/${role.role_id}` : '/access-control/roles', {
        method: role.role_id ? 'PUT' : 'POST',
        body: JSON.stringify(body),
      });
      setRoleDialog({ open: false, role: null });
      setMessage(text.saved);
      loadOverview();
    } catch (e) {
      setError(actionErrorMessage(e));
    }
  };

  const saveMenu = async () => {
    setError('');
    setMessage('');
    try {
      const item = menuDialog.item || {};
      const body = {
        text_key: item.text_key,
        path: item.path,
        page_name: item.page_name,
        permission_key: item.permission_key,
        icon_key: item.icon_key,
        sort_order: Number(item.sort_order || 0),
        is_active: item.is_active ? 1 : 0,
        show_in_menu: item.show_in_menu !== false,
      };
      await fetchJSON(item.menu_id ? `/access-control/menu/${item.menu_id}` : '/access-control/pages', {
        method: item.menu_id ? 'PUT' : 'POST',
        body: JSON.stringify(body),
      });
      setMenuDialog({ open: false, item: null });
      setMessage(text.saved);
      loadOverview();
    } catch (e) {
      setError(actionErrorMessage(e));
    }
  };

  const savePermissions = async () => {
    if (!selectedRole) return;
    setError('');
    setMessage('');
    try {
      const changes = Object.entries(permissionDraft).map(([page_name, can_access]) => ({ page_name, can_access: can_access ? 1 : 0 }));
      if (!changes.length) return;
      await fetchJSON(`/access-control/roles/${selectedRole}/permissions`, {
        method: 'POST',
        body: JSON.stringify({ permissions: changes }),
      });
      setPermissionDraft({});
      setMessage(text.saved);
      loadOverview();
      try {
        window.dispatchEvent(new Event('smart-menu-refresh'));
      } catch (_) {}
    } catch (e) {
      setError(actionErrorMessage(e));
    }
  };

  const setServiceAccess = (pages, checked) => {
    setPermissionDraft((prev) => {
      const next = { ...prev };
      pages.forEach((page) => {
        next[normalizePageName(page.page_name)] = checked;
      });
      return next;
    });
  };

  const setMenuAccess = (item, checked) => {
    const pageNames = (item.page_names || []).map(normalizePageName).filter(Boolean);
    if (!pageNames.length) return;
    setPermissionDraft((prev) => {
      const next = { ...prev };
      pageNames.forEach((pageName) => {
        next[pageName] = checked;
      });
      return next;
    });
  };

  const toggleServiceExpansion = (key) => {
    setExpandedServices((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const restorePageAsMenuLink = (page) => {
    const meta = getPageMeta(page, isArabic);
    setMenuDialog({
      open: true,
      item: {
        text_key: meta.label,
        path: page.path || `/${normalizePageName(page.page_name)}`,
        page_name: normalizePageName(page.page_name),
        permission_key: normalizePageName(page.page_name),
        icon_key: page.icon_key || 'default',
        sort_order: (overview.menu || []).length + 1,
        is_active: true,
        show_in_menu: true,
      },
    });
  };

  const syncPages = async () => {
    setError('');
    setMessage('');
    try {
      await fetchJSON('/access-control/sync-pages', { method: 'POST', body: JSON.stringify({}) });
      setMessage(text.saved);
      loadOverview();
    } catch (e) {
      setError(actionErrorMessage(e));
    }
  };

  const openNewMenu = () =>
    setMenuDialog({
      open: true,
      item: {
        text_key: '',
        path: '',
        page_name: '',
        permission_key: '',
        icon_key: 'default',
        sort_order: (overview.menu || []).length + 1,
        is_active: true,
        show_in_menu: true,
      },
    });

  return (
    <Box className="smart-page-container" sx={{ minHeight: '100vh', bgcolor: COLORS.page, maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.title}</Typography>
            <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>{text.subtitle}</Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button startIcon={<RefreshIcon />} onClick={loadOverview} disabled={loading} sx={{ color: COLORS.gold }}>
              Refresh
            </Button>
            <Button startIcon={<SyncIcon />} variant="outlined" onClick={syncPages} sx={{ borderColor: COLORS.gold, color: COLORS.gold }}>
              {text.sync}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {!isSystemAdminSession && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {text.adminRequired}
        </Alert>
      )}
      <Alert severity="info" sx={{ mb: 2 }}>{text.legacyHidden}</Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}><Metric label={text.roles} value={overview.roles.length} /></Grid>
        <Grid item xs={12} md={3}><Metric label={text.menu} value={overview.menu.length} color={COLORS.blue} soft={COLORS.blueSoft} /></Grid>
        <Grid item xs={12} md={3}><Metric label={text.permissions} value={pageCatalog.length} color={COLORS.green} soft={COLORS.greenSoft} /></Grid>
        <Grid item xs={12} md={3}><Metric label={text.enabled} value={enabledCount} color={COLORS.rose} soft={COLORS.roseSoft} /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable">
            <Tab label={text.roles} />
            <Tab label={text.menu} />
            <Tab label={text.permissions} />
          </Tabs>
          <TextField
            size="small"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={text.search}
            sx={{ width: { xs: '100%', md: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.roles}</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRoleDialog({ open: true, role: { role_name: '', role_name_ar: '' } })} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}>
                {text.addRole}
              </Button>
            </Stack>
            <TableContainer sx={smartTableContainerSx}>
              <Table size="small" sx={smartTableSx}>
                <TableHead sx={smartTableHeadSx}>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{text.roleName}</TableCell>
                    <TableCell>{text.roleNameAr}</TableCell>
                    <TableCell align="center">{text.permissions}</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(overview.roles || []).map((role, index) => (
                    <TableRow key={role.role_id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 900, color: COLORS.text }}>{role.role_name}</TableCell>
                      <TableCell>{role.role_name_ar || '-'}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={(overview.rolePermissions || []).filter((row) => Number(row.role_id) === Number(role.role_id) && Number(row.can_access) === 1).length}
                          sx={{ bgcolor: COLORS.goldSoft, fontWeight: 800 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => setRoleDialog({ open: true, role: { ...role } })}>
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.menu}</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openNewMenu} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}>
                {text.addPage}
              </Button>
            </Stack>
            <TableContainer sx={smartTableContainerSx}>
              <Table size="small" sx={smartTableSx}>
                <TableHead sx={smartTableHeadSx}>
                  <TableRow>
                    <TableCell>{text.icon}</TableCell>
                    <TableCell>{text.textKey}</TableCell>
                    <TableCell>{text.path}</TableCell>
                    <TableCell>{text.pageName}</TableCell>
                    <TableCell align="center">{text.active}</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {menuRows.map((item) => {
                    const Icon = getSidebarIcon(item);
                    return (
                      <TableRow key={item.menu_id} hover sx={{ opacity: legacyPaths.has(normalizePath(item.path)) ? 0.55 : 1 }}>
                        <TableCell width={64}>
                          <Box sx={{ color: COLORS.gold, display: 'flex', '& svg': { width: 22, height: 22 } }}><Icon /></Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 900, color: COLORS.text }}>{item.text_key}</TableCell>
                        <TableCell>{item.path}</TableCell>
                        <TableCell>{item.page_name || '-'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={Number(item.is_active) === 1 ? text.active : text.disabled}
                            sx={{ bgcolor: Number(item.is_active) === 1 ? COLORS.greenSoft : COLORS.roseSoft, fontWeight: 800 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={text.editPage}>
                            <IconButton onClick={() => setMenuDialog({ open: true, item: { ...item, show_in_menu: true } })}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: 2 }}>
            <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                <Box>
                  <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.roleScope}</Typography>
                  <Typography variant="body2" sx={{ color: COLORS.muted }}>{text.roleScopeHint}</Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <Select value={selectedRole} displayEmpty onChange={(event) => setSelectedRole(event.target.value)}>
                    <MenuItem value="">{text.chooseRole}</MenuItem>
                    {(overview.roles || []).map((role) => (
                      <MenuItem key={role.role_id} value={String(role.role_id)}>
                        {isArabic ? role.role_name_ar || role.role_name : role.role_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                {(overview.roles || []).map((role) => {
                  const active = String(selectedRole) === String(role.role_id);
                  const roleEnabledCount = (overview.rolePermissions || []).filter(
                    (row) =>
                      Number(row.role_id) === Number(role.role_id) &&
                      Number(row.can_access) === 1 &&
                      activeMenuPageNames.has(normalizePageName(row.page_name))
                  ).length;
                  return (
                    <Grid item xs={12} sm={6} md={4} xl={2} key={role.role_id}>
                      <Button
                        fullWidth
                        onClick={() => setSelectedRole(String(role.role_id))}
                        sx={{
                          justifyContent: 'space-between',
                          minHeight: 58,
                          border: `1px solid ${active ? COLORS.gold : COLORS.line}`,
                          bgcolor: active ? COLORS.goldSoft : '#fff',
                          color: COLORS.text,
                          fontWeight: 900,
                          px: 1.5,
                        }}
                      >
                        <Box component="span" sx={{ textAlign: 'start', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {isArabic ? role.role_name_ar || role.role_name : role.role_name}
                        </Box>
                        <Chip size="small" label={roleEnabledCount} sx={{ bgcolor: '#fff', fontWeight: 900 }} />
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.permissions}</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Chip
                  label={`${text.visiblePages}: ${visiblePageCatalog.length}`}
                  sx={{ bgcolor: COLORS.greenSoft, color: COLORS.text, fontWeight: 900 }}
                />
                <Chip
                  label={`${text.enabledMenus}: ${enabledMenuCount}/${menuCatalog.length}`}
                  sx={{ bgcolor: COLORS.blueSoft, color: COLORS.text, fontWeight: 900 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => setShowUnusedPages((value) => !value)}
                  sx={{ borderColor: COLORS.line, color: COLORS.text }}
                >
                  {showUnusedPages ? text.hideUnusedPages : `${text.showUnusedPages} (${unusedPageCount})`}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={savePermissions}
                  disabled={!selectedRole || !Object.keys(permissionDraft).length}
                  sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}
                >
                  {text.save}
                </Button>
              </Stack>
            </Stack>

            {!selectedRole ? (
              <Alert severity="info">{text.noRole}</Alert>
            ) : (
              <>
                <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 1.5 }}>
                    <Box>
                      <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.sidebarMenus}</Typography>
                      <Typography variant="body2" sx={{ color: COLORS.muted }}>{text.sidebarMenusHint}</Typography>
                    </Box>
                    <Chip label={`${enabledMenuCount}/${menuCatalog.length}`} sx={{ bgcolor: COLORS.goldSoft, fontWeight: 900 }} />
                  </Stack>
                  <Grid container spacing={1.5}>
                    {menuCatalog.map((item) => {
                      const pageNames = (item.page_names || []).map(normalizePageName).filter(Boolean);
                      const enabledPages = pageNames.filter((pageName) => permissionDraft[pageName] ?? permissionMap.get(pageName) ?? false);
                      const checked = pageNames.length > 0 && enabledPages.length === pageNames.length;
                      const partial = enabledPages.length > 0 && enabledPages.length < pageNames.length;
                      const menuPageLabels = pageNames
                        .slice(0, 3)
                        .map((pageName) => {
                          const page = pageByName.get(pageName) || { page_name: pageName };
                          return getPageMeta(page, isArabic).label;
                        })
                        .join(isArabic ? '، ' : ', ');
                      return (
                        <Grid item xs={12} md={6} xl={4} key={item.menu_id}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              border: `1px solid ${checked ? COLORS.green : partial ? COLORS.gold : COLORS.line}`,
                              borderRadius: 1.5,
                              bgcolor: checked ? COLORS.greenSoft : '#fff',
                              height: '100%',
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                              <Checkbox
                                checked={checked}
                                indeterminate={partial}
                                disabled={!pageNames.length}
                                onChange={(event) => setMenuAccess(item, event.target.checked)}
                                sx={{ color: COLORS.gold, '&.Mui-checked': { color: COLORS.green }, p: 0.25 }}
                              />
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
                                  {item.text_key || item.permission_key || item.path}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: COLORS.muted }}>
                                  {item.path || '-'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: pageNames.length ? COLORS.muted : COLORS.rose, mt: 0.5 }}>
                                  {pageNames.length
                                    ? `${text.linkedPages}: ${menuPageLabels}${pageNames.length > 3 ? ` +${pageNames.length - 3}` : ''}`
                                    : text.menuWithoutPage}
                                </Typography>
                              </Box>
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>

                <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 1 }}>{text.pagesForRole}</Typography>
                <Alert severity="info" sx={{ mb: 2 }}>{text.simpleHint}</Alert>
                {!showUnusedPages && unusedPageCount > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {unusedPageCount} {text.unusedPages}. {text.showUnusedPages}
                  </Alert>
                )}
                <Grid container spacing={2}>
                  {serviceGroups.map((group) => {
                    const checked = group.enabled === group.total;
                    const partial = group.enabled > 0 && group.enabled < group.total;
                    const expanded = Boolean(expandedServices[group.key]);
                    const secondaryPreview = group.secondaryPages
                      .slice(0, 5)
                      .map((page) => getPageMeta(page, isArabic).label)
                      .join(isArabic ? '، ' : ', ');
                    const mainMeta = getPageMeta(group.mainPage, isArabic);
                    return (
                      <Grid item xs={12} md={6} xl={4} key={group.key}>
                        <Paper
                          elevation={0}
                          sx={{
                            ...cardSx,
                            height: '100%',
                            p: 2,
                            borderColor: checked ? COLORS.green : partial ? COLORS.gold : COLORS.line,
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Checkbox
                              checked={checked}
                              indeterminate={partial}
                              onChange={(event) => setServiceAccess(group.pages, event.target.checked)}
                              sx={{ color: COLORS.gold, '&.Mui-checked': { color: COLORS.green } }}
                            />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
                                  {isArabic ? group.ar : group.en}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={`${group.enabled}/${group.total}`}
                                  sx={{ bgcolor: checked ? COLORS.greenSoft : COLORS.goldSoft, fontWeight: 900 }}
                                />
                              </Stack>
                              <Typography variant="caption" sx={{ display: 'block', color: COLORS.muted, mt: 1 }}>
                                {text.mainPage}: <Box component="span" sx={{ color: COLORS.text, fontWeight: 900 }}>{mainMeta.label}</Box>
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: COLORS.muted, mt: 0.5 }}>
                                {mainMeta.description}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: COLORS.muted, mt: 0.5 }}>
                                {text.secondaryPages}: {secondaryPreview || text.noSecondaryPages}
                                {group.secondaryPages.length > 5 ? ` +${group.secondaryPages.length - 5}` : ''}
                              </Typography>
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 1.5 }}>
                                <Typography variant="caption" sx={{ color: COLORS.gold, fontWeight: 900 }}>
                                  {group.enabled} {text.enabledPages}
                                </Typography>
                                <Button
                                  size="small"
                                  endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  onClick={() => toggleServiceExpansion(group.key)}
                                  sx={{ color: COLORS.text, fontWeight: 900 }}
                                >
                                  {expanded ? text.closeBranch : text.customizeBranch}
                                </Button>
                              </Stack>
                            </Box>
                          </Stack>

                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${COLORS.line}` }}>
                              <Typography variant="caption" sx={{ display: 'block', color: COLORS.gold, fontWeight: 900, mb: 1 }}>
                                {text.branchPermissions}
                              </Typography>
                              <Stack spacing={1}>
                                {group.pages.map((page) => {
                                  const normalizedPage = normalizePageName(page.page_name);
                                  const pageChecked = permissionDraft[normalizedPage] ?? permissionMap.get(normalizedPage) ?? false;
                                  const linked = activeMenuPageNames.has(normalizePageName(page.page_name));
                                  const meta = getPageMeta(page, isArabic);
                                  return (
                                    <Paper
                                      key={page.page_name}
                                      elevation={0}
                                      sx={{
                                        p: 1.25,
                                        border: `1px solid ${COLORS.line}`,
                                        borderRadius: 1.5,
                                        bgcolor: linked ? '#fff' : COLORS.roseSoft,
                                      }}
                                    >
                                      <Stack direction="row" spacing={1} alignItems="flex-start">
                                        <Checkbox
                                          size="small"
                                          checked={Boolean(pageChecked)}
                                          onChange={(event) =>
                                            setPermissionDraft((prev) => ({
                                              ...prev,
                                              [normalizedPage]: event.target.checked,
                                            }))
                                          }
                                          sx={{ color: COLORS.gold, '&.Mui-checked': { color: COLORS.green }, p: 0.25 }}
                                        />
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                            <Typography sx={{ color: COLORS.text, fontWeight: 900, fontSize: 14 }}>
                                              {meta.label}
                                            </Typography>
                                            <Chip
                                              size="small"
                                              label={linked ? text.linkedPage : text.unusedPage}
                                              sx={{ bgcolor: linked ? COLORS.greenSoft : COLORS.roseSoft, fontWeight: 800 }}
                                            />
                                          </Stack>
                                          <Typography variant="caption" sx={{ display: 'block', color: COLORS.muted, mt: 0.25 }}>
                                            {meta.description}
                                          </Typography>
                                          <Typography variant="caption" sx={{ display: 'block', color: COLORS.muted, mt: 0.25 }}>
                                            {text.technicalName}: {page.page_name}
                                          </Typography>
                                          {!linked && (
                                            <Button
                                              size="small"
                                              startIcon={<AddIcon />}
                                              onClick={() => restorePageAsMenuLink(page)}
                                              sx={{ color: COLORS.gold, fontWeight: 900, mt: 0.5, px: 0 }}
                                            >
                                              {text.restoreAsMenuLink}
                                            </Button>
                                          )}
                                        </Box>
                                      </Stack>
                                    </Paper>
                                  );
                                })}
                              </Stack>
                            </Box>
                          </Collapse>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}
          </Box>
        )}
      </Paper>

      <Dialog open={roleDialog.open} onClose={() => setRoleDialog({ open: false, role: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{roleDialog.role?.role_id ? text.editRole : text.addRole}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={text.roleName}
              value={roleDialog.role?.role_name || ''}
              onChange={(event) => setRoleDialog((state) => ({ ...state, role: { ...state.role, role_name: event.target.value } }))}
              fullWidth
            />
            <TextField
              label={text.roleNameAr}
              value={roleDialog.role?.role_name_ar || ''}
              onChange={(event) => setRoleDialog((state) => ({ ...state, role: { ...state.role, role_name_ar: event.target.value } }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog({ open: false, role: null })}>{text.cancel}</Button>
          <Button variant="contained" onClick={saveRole} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.save}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={menuDialog.open} onClose={() => setMenuDialog({ open: false, item: null })} maxWidth="md" fullWidth>
        <DialogTitle>{menuDialog.item?.menu_id ? text.editPage : text.addPage}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField label={text.textKey} fullWidth value={menuDialog.item?.text_key || ''} onChange={(event) => setMenuDialog((state) => ({ ...state, item: { ...state.item, text_key: event.target.value } }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label={text.path} fullWidth value={menuDialog.item?.path || ''} onChange={(event) => setMenuDialog((state) => ({ ...state, item: { ...state.item, path: event.target.value } }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label={text.pageName} fullWidth value={menuDialog.item?.page_name || ''} onChange={(event) => setMenuDialog((state) => ({ ...state, item: { ...state.item, page_name: event.target.value } }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label={text.permissionKey} fullWidth value={menuDialog.item?.permission_key || ''} onChange={(event) => setMenuDialog((state) => ({ ...state, item: { ...state.item, permission_key: event.target.value } }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={text.icon}
                fullWidth
                value={menuDialog.item?.icon_key || 'default'}
                onClick={() => setIconOpen(true)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField label={text.sort} type="number" fullWidth value={menuDialog.item?.sort_order || 0} onChange={(event) => setMenuDialog((state) => ({ ...state, item: { ...state.item, sort_order: Number(event.target.value) } }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={<Checkbox checked={menuDialog.item?.is_active !== false && Number(menuDialog.item?.is_active ?? 1) === 1} onChange={(event) => setMenuDialog((state) => ({ ...state, item: { ...state.item, is_active: event.target.checked } }))} />}
                label={text.active}
              />
            </Grid>
            {!menuDialog.item?.menu_id && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={menuDialog.item?.show_in_menu !== false} onChange={(event) => setMenuDialog((state) => ({ ...state, item: { ...state.item, show_in_menu: event.target.checked } }))} />}
                  label={text.shownInMenu}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMenuDialog({ open: false, item: null })}>{text.cancel}</Button>
          <Button variant="contained" onClick={saveMenu} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.save}</Button>
        </DialogActions>
      </Dialog>

      <IconPicker
        open={iconOpen}
        value={menuDialog.item?.icon_key}
        onClose={() => setIconOpen(false)}
        onSelect={(key) => setMenuDialog((state) => ({ ...state, item: { ...state.item, icon_key: key } }))}
      />
    </Box>
  );
}
